// src/hooks/useGeolocation.js
import { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { updateMyLocation } from '../store/slices/driverSlice';

const useGeolocation = (options = {}) => {
  const dispatch = useDispatch();
  const watchIdRef = useRef(null);
  const lastUpdateRef = useRef(0);

  const [position, setPosition] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const {
    enableHighAccuracy = true,
    timeout = 10000,
    maximumAge = 0,
    watchPosition = false,
    updateInterval = 5000, // Min time between server updates
    sendToServer = true
  } = options;

  const updateServerPosition = useCallback((coords) => {
    const now = Date.now();
    if (sendToServer && now - lastUpdateRef.current > updateInterval) {
      lastUpdateRef.current = now;
      dispatch(updateMyLocation({
        latitude: coords.latitude,
        longitude: coords.longitude,
        accuracy: coords.accuracy
      })).catch(err => {
        console.warn('Failed to update server position:', err);
      });
    }
  }, [dispatch, sendToServer, updateInterval]);

  const handleSuccess = useCallback((pos) => {
    const coords = {
      latitude: pos.coords.latitude,
      longitude: pos.coords.longitude,
      accuracy: pos.coords.accuracy,
      altitude: pos.coords.altitude,
      altitudeAccuracy: pos.coords.altitudeAccuracy,
      heading: pos.coords.heading,
      speed: pos.coords.speed,
      timestamp: pos.timestamp
    };

    setPosition(coords);
    setError(null);
    setLoading(false);

    updateServerPosition(coords);
  }, [updateServerPosition]);

  const handleError = useCallback((err) => {
    let errorMessage;
    switch (err.code) {
      case err.PERMISSION_DENIED:
        errorMessage = 'Accès à la localisation refusé';
        break;
      case err.POSITION_UNAVAILABLE:
        errorMessage = 'Position indisponible';
        break;
      case err.TIMEOUT:
        errorMessage = 'Délai de localisation dépassé';
        break;
      default:
        errorMessage = 'Erreur de géolocalisation';
    }
    setError(errorMessage);
    setLoading(false);
    console.warn('Geolocation error:', err);
  }, []);

  // Get current position once
  const getCurrentPosition = useCallback(() => {
    if (!navigator.geolocation) {
      setError('Géolocalisation non supportée');
      setLoading(false);
      return;
    }

    setLoading(true);
    navigator.geolocation.getCurrentPosition(handleSuccess, handleError, {
      enableHighAccuracy,
      timeout,
      maximumAge
    });
  }, [enableHighAccuracy, timeout, maximumAge, handleSuccess, handleError]);

  // Watch position continuously
  useEffect(() => {
    if (!navigator.geolocation) {
      setError('Géolocalisation non supportée');
      setLoading(false);
      return;
    }

    // Always get initial position
    getCurrentPosition();

    // Set up watch if requested
    if (watchPosition) {
      watchIdRef.current = navigator.geolocation.watchPosition(
        handleSuccess,
        handleError,
        {
          enableHighAccuracy,
          timeout,
          maximumAge: 0 // Always get fresh position when watching
        }
      );
    }

    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, [watchPosition, enableHighAccuracy, timeout, maximumAge, getCurrentPosition, handleSuccess, handleError]);

  // Recenter function for map
  const recenter = useCallback(() => {
    getCurrentPosition();
  }, [getCurrentPosition]);

  return {
    position,
    error,
    loading,
    isWatching: watchIdRef.current !== null,
    getCurrentPosition,
    recenter
  };
};

export default useGeolocation;
